import React, { useState, useEffect } from "react";
import { Clock } from "lucide-react";

export function formatTime12(time24) {
  if (!time24) return "12:00 PM";
  const [hStr, mStr] = time24.split(":");
  let h = parseInt(hStr, 10);
  const m = mStr || "00";
  if (isNaN(h)) return time24;
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${String(h).padStart(2, "0")}:${m} ${ampm}`;
}

export function parseTimeTo24(hour, minute, ampm) {
  let h = parseInt(hour, 10);
  if (ampm === "PM" && h < 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

const HOURS_CLOCK = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES_CLOCK = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

export default function MobileTimePickerModal({
  isOpen,
  onClose,
  initialTime = "06:00",
  onSelectTime,
  title = "Select Time"
}) {
  const [mode, setMode] = useState("hour"); // "hour" or "minute"
  const [selectedHour, setSelectedHour] = useState("06");
  const [selectedMinute, setSelectedMinute] = useState("00");
  const [ampm, setAmpm] = useState("AM");

  useEffect(() => {
    if (isOpen && initialTime) {
      setMode("hour");
      const [hStr, mStr] = initialTime.split(":");
      let h = parseInt(hStr || "6", 10);
      const isPm = h >= 12;
      setAmpm(isPm ? "PM" : "AM");
      h = h % 12;
      if (h === 0) h = 12;
      setSelectedHour(String(h).padStart(2, "0"));
      setSelectedMinute(String(mStr || "00").padStart(2, "0"));
    }
  }, [isOpen, initialTime]);

  if (!isOpen) return null;

  const handleHourClick = (h) => {
    const formatted = String(h).padStart(2, "0");
    setSelectedHour(formatted);
    // Transition automatically to minute selection mode
    setMode("minute");
  };

  const handleMinuteClick = (m) => {
    const formatted = String(m).padStart(2, "0");
    setSelectedMinute(formatted);
  };

  const handleOk = () => {
    const time24 = parseTimeTo24(selectedHour, selectedMinute, ampm);
    if (onSelectTime) {
      onSelectTime(time24);
    }
    onClose();
  };

  // Clock Geometry for 12 positions around a circle (radius = 90px, center = 120px)
  const RADIUS = 88;
  const CENTER = 115;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      data-testid="mobile-time-picker-modal"
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-xs" onClick={onClose} />

      <div
        className="relative bg-white w-full max-w-xs rounded-3xl shadow-2xl border border-pink-200 z-10 overflow-hidden flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Header with Selected Time & AM/PM Toggle */}
        <div className="bg-gradient-to-r from-pink-950 via-pink-900 to-rose-900 text-white p-5 w-full flex items-center justify-between">
          <div className="space-y-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-pink-200 block">
              {title}
            </span>
            <div className="flex items-baseline gap-1 text-2xl font-black tracking-tight">
              <button
                type="button"
                data-testid="time-display-hour"
                onClick={() => setMode("hour")}
                className={`transition-colors ${
                  mode === "hour" ? "text-white underline decoration-pink-400" : "text-pink-300"
                }`}
              >
                {selectedHour}
              </button>
              <span>:</span>
              <button
                type="button"
                data-testid="time-display-minute"
                onClick={() => setMode("minute")}
                className={`transition-colors ${
                  mode === "minute" ? "text-white underline decoration-pink-400" : "text-pink-300"
                }`}
              >
                {selectedMinute}
              </button>
            </div>
          </div>

          {/* AM / PM Toggle Buttons */}
          <div className="flex rounded-xl bg-black/20 p-1 border border-white/20">
            <button
              type="button"
              data-testid="toggle-am-btn"
              onClick={() => setAmpm("AM")}
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                ampm === "AM" ? "bg-white text-pink-950 shadow-sm" : "text-pink-200 hover:text-white"
              }`}
            >
              AM
            </button>
            <button
              type="button"
              data-testid="toggle-pm-btn"
              onClick={() => setAmpm("PM")}
              className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all ${
                ampm === "PM" ? "bg-white text-pink-950 shadow-sm" : "text-pink-200 hover:text-white"
              }`}
            >
              PM
            </button>
          </div>
        </div>

        {/* Mode Indicator Tab */}
        <div className="py-2.5 px-4 w-full bg-pink-50/60 border-b border-pink-100 flex items-center justify-between text-xs font-bold text-gray-700">
          <span>{mode === "hour" ? "1. Select Hour (1 - 12)" : "2. Select Minutes (00 - 55)"}</span>
          <button
            type="button"
            onClick={() => setMode(mode === "hour" ? "minute" : "hour")}
            className="text-pink-900 font-extrabold hover:underline"
          >
            Switch to {mode === "hour" ? "Minutes" : "Hour"}
          </button>
        </div>

        {/* Round Analog Clock Face */}
        <div className="p-4 flex items-center justify-center">
          <div
            className="w-[230px] h-[230px] rounded-full bg-pink-50/70 border-2 border-pink-200 relative shadow-inner flex items-center justify-center select-none"
            data-testid="analog-clock-face"
          >
            {/* Center Pin */}
            <div className="w-3 h-3 rounded-full bg-pink-900 z-10" />

            {/* Clock Numbers arranged circular */}
            {mode === "hour"
              ? HOURS_CLOCK.map((h, i) => {
                  const angle = (i * 30 - 60) * (Math.PI / 180);
                  const x = CENTER + RADIUS * Math.cos(angle);
                  const y = CENTER + RADIUS * Math.sin(angle);
                  const isSelected = parseInt(selectedHour, 10) === h;

                  return (
                    <button
                      key={h}
                      type="button"
                      data-testid={`clock-hour-${h}`}
                      onClick={() => handleHourClick(h)}
                      style={{
                        position: "absolute",
                        left: `${x}px`,
                        top: `${y}px`,
                        transform: "translate(-50%, -50%)"
                      }}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                        isSelected
                          ? "bg-pink-900 text-white shadow-md scale-110"
                          : "text-gray-800 hover:bg-pink-200/80"
                      }`}
                    >
                      {h}
                    </button>
                  );
                })
              : MINUTES_CLOCK.map((m, i) => {
                  const angle = (i * 30 - 60) * (Math.PI / 180);
                  const x = CENTER + RADIUS * Math.cos(angle);
                  const y = CENTER + RADIUS * Math.sin(angle);
                  const isSelected = parseInt(selectedMinute, 10) === m;

                  return (
                    <button
                      key={m}
                      type="button"
                      data-testid={`clock-minute-${m}`}
                      onClick={() => handleMinuteClick(m)}
                      style={{
                        position: "absolute",
                        left: `${x}px`,
                        top: `${y}px`,
                        transform: "translate(-50%, -50%)"
                      }}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                        isSelected
                          ? "bg-pink-900 text-white shadow-md scale-110"
                          : "text-gray-800 hover:bg-pink-200/80"
                      }`}
                    >
                      {String(m).padStart(2, "0")}
                    </button>
                  );
                })}
          </div>
        </div>

        {/* Bottom Actions: Cancel & OK */}
        <div className="p-4 bg-gray-50 border-t border-pink-100 w-full flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid="clock-cancel-btn"
            onClick={onClose}
            className="px-5 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="clock-ok-btn"
            onClick={handleOk}
            className="px-6 py-2 rounded-xl bg-pink-950 hover:bg-pink-900 text-white text-xs font-bold shadow-sm transition-all"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
